/**
 * Recurring Tickets — API client.
 *
 * Contract: `.claude/projects/control-center/recurring-tickets/API_CONTRACT.md`
 * Bug register: `.claude/projects/control-center/recurring-tickets/BACKEND_BUGS_QA.md`
 *
 * Conventions (matched to existing `src/controller/api/service-desk/ticket/ticket.ts`):
 *  - Use `axiosClient` from `utilz`.
 *  - Inspect `response.data.success` + `response.data.statusCode === 200` to determine
 *    business-logic outcome (DeskDay uses HTTP 200 even for business errors).
 *  - On non-success, `throw response` (raw AxiosResponse) — NOT `throw new Error()`.
 *  - Catch → `return apiErrorHandler(error as AxiosError | AxiosResponse)`.
 *    `apiErrorHandler` actually throws an `APIRequestError`; the `return` keeps the
 *    existing project style and satisfies `never` type flow.
 *  - Base URL: `/v1/servicedesk/recurring-tickets` (from API_CONTRACT).
 *  - `msp_id` is NOT sent in body — backend resolves it from the JWT.
 *  - NO toasts in this layer. Toasts live in the UI layer only.
 */

import type { AxiosError, AxiosResponse } from "axios";
import type {
  BulkDeleteRecurringTicketsResponse,
  BulkPauseRecurringTicketsResponse,
  BulkResumeRecurringTicketsResponse,
  BulkSkipNextCycleRecurringTicketsResponse,
  CreateRecurringTicketNowResponse,
  CreateRecurringTicketPayload,
  CreateRecurringTicketResponse,
  DeleteRecurringTicketResponse,
  GetRecurringTicketAuditTrailResponse,
  GetRecurringTicketByIdResponse,
  GetRecurringTicketsQuery,
  ListRecurringTicketsResponse,
  PauseRecurringTicketPayload,
  PauseRecurringTicketResponse,
  ResumeRecurringTicketResponse,
  SkipRecurringTicketDatesPayload,
  SkipRecurringTicketDatesResponse,
  UpdateRecurringTicketPayload,
  UpdateRecurringTicketResponse,
} from "shared";
import { axiosClient } from "utilz";
import { apiErrorHandler, generateUrlString } from "utilz/helpers";

import { stripStatusFromPatch } from "./recurringTicketHelpers";

const BASE_URL = "/v1/servicedesk/recurring-tickets";
const AUDIT_BASE_URL = import.meta.env.VITE_APP_AUDIT_BASE_URL;

// ----------------------------------------------------------------------------
// 1. GET list
// ----------------------------------------------------------------------------

/**
 * GET `/v1/servicedesk/recurring-tickets`
 *
 * Backend expects individual query params — array filters as JSON-stringified
 * arrays (e.g. `status=["active","paused"]`), date ranges as `_from`/`_to` pairs.
 *
 * Response envelope is non-standard — caller should route through `unwrapList<T>()`
 * (see `recurringTicketHelpers.ts`).
 */
export const getRecurringTicketsList = async (
  params: GetRecurringTicketsQuery = {},
): Promise<ListRecurringTicketsResponse> => {
  try {
    const data: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value == null || value === "") continue;
      data[key] = String(value);
    }
    const url = await generateUrlString({ baseUrl: BASE_URL, data });
    const response = await axiosClient.get(url);
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as ListRecurringTicketsResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// ----------------------------------------------------------------------------
// 2. GET by id
// ----------------------------------------------------------------------------

export const getRecurringTicketById = async (
  mappingId: string,
): Promise<GetRecurringTicketByIdResponse> => {
  try {
    const url = `${BASE_URL}/${mappingId}`;
    const response = await axiosClient.get(url);
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as GetRecurringTicketByIdResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// ----------------------------------------------------------------------------
// 3. POST create
// ----------------------------------------------------------------------------

/**
 * POST `/v1/servicedesk/recurring-tickets`
 *
 * Note: DeskDay returns HTTP 200 on create (NOT 201). Success check matches 200.
 * Backend omits `checklists`, `tags`, `attachments`, `assignees`, `schedulings` from the
 * create response — callers that need those must follow up with `getRecurringTicketById`.
 */
export const createRecurringTicket = async (
  payload: CreateRecurringTicketPayload,
): Promise<CreateRecurringTicketResponse> => {
  try {
    const response = await axiosClient.post(BASE_URL, payload);
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as CreateRecurringTicketResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// ----------------------------------------------------------------------------
// 4. PATCH update
// ----------------------------------------------------------------------------

/**
 * PATCH `/v1/servicedesk/recurring-tickets/:id`
 *
 * BUG-10: strip `status` client-side — backend silently accepts it instead of 422-ing.
 * BUG-12 hardening: if the sanitised payload is empty, short-circuit with a synthetic
 * error envelope rather than firing a no-op PATCH. The form layer (Phase 5/6) should
 * also block empty submits; this is the defensive belt.
 */
export const updateRecurringTicket = async (
  mappingId: string,
  payload: UpdateRecurringTicketPayload,
): Promise<UpdateRecurringTicketResponse> => {
  // BUG-10: strip status before sending.
  const sanitised = stripStatusFromPatch(payload);

  // BUG-12 hardening: do not fire a no-op PATCH.
  if (Object.keys(sanitised).length === 0) {
    // Synthesize a response shape that `apiErrorHandler` knows how to parse.
    const syntheticErrorResponse = {
      data: {
        success: false,
        statusCode: 400,
        data: null,
        message: "No changes to save",
      },
      headers: {},
      status: 200,
      statusText: "OK",
      config: {},
    } as unknown as AxiosResponse;
    return apiErrorHandler(syntheticErrorResponse);
  }

  try {
    const url = `${BASE_URL}/${mappingId}`;
    const response = await axiosClient.patch(url, sanitised);
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as UpdateRecurringTicketResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// ----------------------------------------------------------------------------
// 5. PATCH pause
// ----------------------------------------------------------------------------

/**
 * PATCH `/v1/servicedesk/recurring-tickets/:id/pause`
 *
 * BUG-11: conflict (already paused) returns HTTP 200 + `statusCode: 400` in body with
 * the canonical message. The API layer does NOT branch on this — it simply `throw`s the
 * response and lets `apiErrorHandler` surface the message to the caller. The mutation
 * hook / UI layer runs `matchPauseConflict()` on the thrown error to pick the right copy.
 */
export const pauseRecurringTicket = async (
  mappingId: string,
  body?: PauseRecurringTicketPayload,
): Promise<PauseRecurringTicketResponse> => {
  try {
    const url = `${BASE_URL}/${mappingId}/pause`;
    const response = await axiosClient.patch(url, body ?? {});
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as PauseRecurringTicketResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// ----------------------------------------------------------------------------
// 6. PATCH resume
// ----------------------------------------------------------------------------

/**
 * PATCH `/v1/servicedesk/recurring-tickets/:id/resume`
 *
 * Resume has no body. BUG-11 applies for the "not paused" conflict. Note that
 * backend still does NOT recalculate `next_run` on resume (BullMQ no-op — BUG-01),
 * so UI callers must not rely on the returned `next_run`.
 */
export const resumeRecurringTicket = async (
  mappingId: string,
): Promise<ResumeRecurringTicketResponse> => {
  try {
    const url = `${BASE_URL}/${mappingId}/resume`;
    // Empty body — some axios setups add a problematic content-type when sending `{}`,
    // but the existing project axiosClient defaults are fine; mirror the service-desk pattern.
    const response = await axiosClient.patch(url, {});
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as ResumeRecurringTicketResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// ----------------------------------------------------------------------------
// 7. DELETE (soft-delete)
// ----------------------------------------------------------------------------

/**
 * DELETE `/v1/servicedesk/recurring-tickets/:id` — soft-delete.
 * Backend returns the full detail record with `is_deleted: true` and `status: "inactive"`.
 */
export const deleteRecurringTicket = async (
  mappingId: string,
): Promise<DeleteRecurringTicketResponse> => {
  try {
    const url = `${BASE_URL}/${mappingId}`;
    const response = await axiosClient.delete(url);
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as DeleteRecurringTicketResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// ----------------------------------------------------------------------------
// 8. PATCH bulk pause
// ----------------------------------------------------------------------------

/**
 * PATCH `/v1/servicedesk/recurring-tickets/bulk/pause`
 *
 * Idempotent: already-paused tickets are skipped (not failed).
 */
export const bulkPauseRecurringTickets = async (
  recurringTicketIds: string[],
): Promise<BulkPauseRecurringTicketsResponse> => {
  try {
    const url = `${BASE_URL}/bulk/pause`;
    const response = await axiosClient.patch(url, {
      recurring_ticket_ids: recurringTicketIds,
    });
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as BulkPauseRecurringTicketsResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// ----------------------------------------------------------------------------
// 9. PATCH bulk resume
// ----------------------------------------------------------------------------

/**
 * PATCH `/v1/servicedesk/recurring-tickets/bulk/resume`
 *
 * Idempotent: already-active tickets are skipped (not failed).
 */
export const bulkResumeRecurringTickets = async (
  recurringTicketIds: string[],
): Promise<BulkResumeRecurringTicketsResponse> => {
  try {
    const url = `${BASE_URL}/bulk/resume`;
    const response = await axiosClient.patch(url, {
      recurring_ticket_ids: recurringTicketIds,
    });
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as BulkResumeRecurringTicketsResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// ----------------------------------------------------------------------------
// 10. POST bulk delete
// ----------------------------------------------------------------------------

/**
 * POST `/v1/servicedesk/recurring-tickets/bulk/delete`
 *
 * Soft-deletes multiple recurring tickets. No skipped array — tickets are
 * either deleted or failed.
 */
export const bulkDeleteRecurringTickets = async (
  recurringTicketIds: string[],
): Promise<BulkDeleteRecurringTicketsResponse> => {
  try {
    const url = `${BASE_URL}/bulk/delete`;
    const response = await axiosClient.post(url, {
      recurring_ticket_ids: recurringTicketIds,
    });
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as BulkDeleteRecurringTicketsResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// ----------------------------------------------------------------------------
// 10b. PATCH bulk skip-next-cycle
// ----------------------------------------------------------------------------

/**
 * PATCH `/v1/servicedesk/recurring-tickets/bulk/skip-next-cycle`
 *
 * Skips the next scheduled run for each recurring ticket. Idempotent for
 * tickets whose next cycle is already skipped/unreachable (backend places
 * those in `skipped`, not `failed`).
 */
export const bulkSkipNextCycleRecurringTickets = async (
  recurringTicketIds: string[],
): Promise<BulkSkipNextCycleRecurringTicketsResponse> => {
  try {
    const url = `${BASE_URL}/bulk/skip-next-cycle`;
    const response = await axiosClient.patch(url, {
      recurring_ticket_ids: recurringTicketIds,
    });
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as BulkSkipNextCycleRecurringTicketsResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// ----------------------------------------------------------------------------
// 10c. POST skip-dates (skip specific scheduled dates)
// ----------------------------------------------------------------------------

/**
 * POST `/v1/servicedesk/recurring-tickets/:id/skip-dates`
 *
 * Skips specific scheduled dates for a recurring ticket.
 * Unlike bulk skip-next-cycle, this targets exact run times.
 */
export const skipRecurringTicketDates = async (
  recurringTicketId: string,
  payload: SkipRecurringTicketDatesPayload,
): Promise<SkipRecurringTicketDatesResponse> => {
  try {
    const url = `${BASE_URL}/${recurringTicketId}/skip-dates`;
    const response = await axiosClient.post(url, payload);
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as SkipRecurringTicketDatesResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// ----------------------------------------------------------------------------
// 11. POST create-now (manually trigger ticket generation)
// ----------------------------------------------------------------------------

/**
 * POST `/servicedesk/recurring-ticket/create-now/:recurring_ticket_id`
 *
 * Manually triggers immediate ticket generation from the recurring ticket
 * template. Path is intentionally singular and without `/v1` — matches the
 * backend contract (see API_CONTRACT.md).
 */
export const createRecurringTicketNow = async (
  mappingId: string,
): Promise<CreateRecurringTicketNowResponse> => {
  try {
    const url = `/servicedesk/recurring-ticket/create-now/${mappingId}`;
    const response = await axiosClient.post(url, {});
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as CreateRecurringTicketNowResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

// ----------------------------------------------------------------------------
// 12. GET audit trail
// ----------------------------------------------------------------------------

/**
 * GET `/v1/servicedesk/recurring-tickets/:id/audit-trail`
 */
export const getRecurringTicketAuditTrail = async (
  mappingId: string,
): Promise<GetRecurringTicketAuditTrailResponse> => {
  try {
    const url = `${AUDIT_BASE_URL}/v1/recurring-tickets/${mappingId}/audit-trail`;
    const response = await axiosClient.get(url);
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData as GetRecurringTicketAuditTrailResponse;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
